import { useSyncExternalStore } from 'react';
import { chatDrawerFit, isBelowMobile } from '@wundo/ui';
import { clearChatUnread } from './chatUnread.ts';
import { currentChatContentMin } from './chatContentMin.ts';
import { chatDrawerWidth } from './chatDrawerWidth.ts';

const STORAGE_KEY = 'wundo.chat.open';

/**
 * What the drawer is doing.
 *
 * Two states, not three: on a desktop the drawer is never gone, only collapsed
 * to its strip, and on a phone there is no strip — so collapsed is what closed
 * looks like there. The presentation is the drawer's business; this is only
 * whether the user is in it.
 */
export type ChatDrawerState = 'open' | 'collapsed';

/*
 * Everyone currently asking. There is one drawer, so there is one answer: the
 * agenda collapses the chat when it opens a meeting beside it, and the shell has
 * to hear that rather than keep drawing a drawer nobody asked for any more.
 */
const listeners = new Set<() => void>();

/*
 * The state itself lives here rather than in whoever renders the drawer: a page
 * can open the chat from its mount effect, which runs before the layout above
 * it has started listening — a broadcast alone would land on nobody. Held as a
 * value, the layout reads it whenever it does arrive.
 */
let current: ChatDrawerState | null = null;

/** What the drawer is doing right now, whoever asks. */
export function currentChatDrawerState(): ChatDrawerState {
  current ??= isBelowMobile() ? 'collapsed' : readStored();
  return current;
}

/**
 * Back to the not-yet-asked state, so the next read starts from storage the way
 * a fresh load does. Tests simulate a reload with this; the app never does —
 * within a visit, what the module holds is the truth.
 */
export function forgetChatDrawerState(): void {
  current = null;
}

function transition(state: ChatDrawerState): void {
  current = state;
  /* Opening it is seeing what came in while it was away, however it was opened. */
  if (state === 'open') clearChatUnread();
  listeners.forEach((listener) => listener());
}

/*
 * Same treatment as the rail: reading storage can throw outright in a browser
 * that blocks site data, and a chat that fails to render is worse than one that
 * forgets whether it was open.
 *
 * `true` is what the older boolean wrote. Read rather than migrated: a value
 * written by a version that has been superseded is still the user's answer, and
 * rewriting storage on read would mean every reader needs write access.
 */
function readStored(): ChatDrawerState {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === 'open' || stored === 'true' ? 'open' : 'collapsed';
  } catch {
    return 'collapsed';
  }
}

function writeStored(state: ChatDrawerState): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, state);
  } catch {
    /* The drawer still works; it just forgets between visits. */
  }
}

/**
 * Open or collapse the drawer from anywhere, whether or not the caller draws it.
 *
 * The agenda uses this: a meeting opening on the right edge takes that edge
 * from the chat, and the shell has to hear it.
 */
export function setChatDrawerState(state: ChatDrawerState): void {
  writeStored(state);
  transition(state);
}

/**
 * Open the drawer for as long as a page is on screen. Returns the way to put it
 * back.
 *
 * Nothing is written down: a page opening the chat by itself is not the user
 * asking for it, so it must not answer for every other page. Leaving goes back
 * to whatever is remembered at that moment rather than to what was remembered
 * on arrival — collapsing it here is the user asking, and that answer stands.
 */
export function openChatDrawerForPage(): () => void {
  transition('open');

  return () => {
    transition(isBelowMobile() ? 'collapsed' : readStored());
  };
}

/**
 * Whether the drawer would stand beside the page rather than over it.
 *
 * A page that opens the chat without being asked has to check: too narrow for
 * both, and the drawer covers the page the user came to read. The same sum the
 * shell lays out with, and the same floor — a page that has said its content
 * reads fine in a narrow column is answered on that, or it would refuse to open
 * beside a page it fits beside perfectly well.
 */
export function chatDrawerFitsBesidePage(contentMin?: number): boolean {
  if (isBelowMobile()) return false;

  return chatDrawerFit({
    wanted: chatDrawerWidth(),
    contentMin: contentMin ?? currentChatContentMin(),
  }).pushes;
}

/** Called whenever anyone opens or collapses it. Returns the way to stop listening. */
export function onChatDrawerStateChange(listener: () => void): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

/**
 * What the drawer is doing, remembered across pages and visits.
 *
 * Collapsed by default — the strip is always there to be clicked, but the
 * conversation is something the user asks for rather than something that greets
 * them. A phone starts collapsed whatever is remembered: there the drawer opens
 * over the whole page, and covering the page somebody just navigated to is not
 * a greeting either.
 *
 * A fresh mount reads whatever the module already holds — which a page's own
 * mount effect may have set before this subscription existed; from then on it
 * follows whoever sets it, including the agenda.
 */
export function useChatDrawerState(): [
  ChatDrawerState,
  (state: ChatDrawerState) => void,
] {
  const state = useSyncExternalStore(
    onChatDrawerStateChange,
    currentChatDrawerState,
    () => 'collapsed' as const,
  );

  return [state, setChatDrawerState];
}
